import { useEffect, useState } from "react";
import Navbar from "../Navbar";
import Leaderboard from "./Leaderboard";
import { Link } from "react-router-dom";

/**
 * Type to represent the filters state
 */
type filtersType = {
  mode: string[];
  difficulty: string[];
};

/**
 * Type to represent a leaderboard
 */
type leaderboardType = {
  mode: string;
  difficulty: string;
};

/**
 * Type to represent a filter button
 */
type filterButton = {
  name: string;
  getValue: () => boolean;
  setValue: () => void;
  type: "mode" | "difficulty";
};

/**
 * Renders the "/leaderboards" page
 * @returns html containing ...
 */
const Leaderboards = () => {
  useEffect(() => {
    document.title = "Leaderboards - FPS Aim Trainer";
  }, []);

  const [filters, setFilters] = useState<filtersType>({
    mode: [],
    difficulty: [],
  }); // will represent the current filters

  /**
   * Returns whether a filter is activated
   * @param name is the name of the filter
   * @param category is the filter's category
   * @returns a boolean
   */
  const getValue = (name: string, category: "mode" | "difficulty") => {
    return filters[category].indexOf(name) !== -1;
  };

  /**
   * Toggles if a filter is enabled
   * @param val is the name of the filter
   * @param category is the filter's category
   */
  const setValue = (val: string, category: "mode" | "difficulty") => {
    if (getValue(val, category)) {
      // if the filter is currently activated
      setFilters((cur) => {
        cur = JSON.parse(JSON.stringify(cur)); // needed to get a new memory reference to cur
        cur[category] = cur[category].filter((item) => item !== val); // remove any values that match
        return cur;
      });
    } else {
      setFilters((cur) => {
        cur = JSON.parse(JSON.stringify(cur)); // needed to get a new memory reference to cur
        cur[category].push(val); // add the value
        return cur;
      });
    }
  };

  // buttons used to filter the leaderboards
  const filterButtons: filterButton[] = [
    {
      name: "Iron Sights",
      getValue: () => getValue("Iron Sights", "mode"),
      setValue: () => setValue("Iron Sights", "mode"),
      type: "mode",
    },
    {
      name: "Duels",
      getValue: () => getValue("Duels", "mode"),
      setValue: () => setValue("Duels", "mode"),
      type: "mode",
    },
    {
      name: "Easy",
      getValue: () => getValue("Easy", "difficulty"),
      setValue: () => setValue("Easy", "difficulty"),
      type: "difficulty",
    },
    {
      name: "Medium",
      getValue: () => getValue("Medium", "difficulty"),
      setValue: () => setValue("Medium", "difficulty"),
      type: "difficulty",
    },
    {
      name: "Hard",
      getValue: () => getValue("Hard", "difficulty"),
      setValue: () => setValue("Hard", "difficulty"),
      type: "difficulty",
    },
  ];

  const [leaderboards, setLeaderboards] = useState<leaderboardType[]>([]); // all possible leaderboards

  useEffect(() => {
    let options: filtersType = {
      mode: [],
      difficulty: [],
    }; // will hold every different option
    filterButtons.forEach((button) => {
      options[button.type].push(button.name);
    }); // populate options with every different option
    let optionList = [options.mode, options.difficulty]; // options in list form
    let out: leaderboardType[] = []; // new value of leaderboards

    /**
     * Recursive function to find every combination of options
     * @param i is the corresponding index in options list
     * @param option is the current option being built
     */
    const createOptions = (i: number, option: string[]) => {
      // if there are still options to add
      if (i < optionList.length) {
        // create an option for every possibility (in a tree like fashion)
        for (let j = 0; j < optionList[i].length; j++) {
          createOptions(i + 1, [...option, optionList[i][j]]);
        }
      } else {
        // new option
        let o = {
          mode: option[0],
          difficulty: option[1],
        };
        out.push(o);
      }
    };

    createOptions(0, []); // create options starting at i = 0 and an empty list
    setLeaderboards(out); // set the leaderboards with out
  }, []);

  return (
    <div className="h-screen flex flex-col">
      <Navbar />
      <div className="w-screen flex-1">
        <div className="p-10">
          <p className="text-5xl font-semibold text-indigo-500">
            All Leaderboards
          </p>
          <p className="font-semibold text-3xl mt-5 mb-2">Filters</p>
          <div className="flex justify-between align-center">
            <div className="flex mb-4 flex-wrap gap-3">
              {filterButtons.map((filter) => (
                <button
                  className={
                    "rounded-full px-3 py-2 text-lg border-black border-2 font-semibold " +
                    (filter.getValue()
                      ? "bg-indigo-500 text-white"
                      : "bg-gray-100 hover:bg-gray-200")
                  }
                  key={filter.name}
                  onClick={() => filter.setValue()}
                >
                  {filter.name}
                </button>
              ))}
            </div>
            <Link to="/data" className="hidden lg:block">
              <button className="outline outline-red-500 outline-2 hover:bg-red-500 text-md rounded-md text-center w-40 py-2 mb-4 font-semibold">
                View Your Data
              </button>
            </Link>
          </div>
          <div className="bg-gray-800 h-0.5 mt-2 mb-4" />
          <div className="flex flex-row justify-around flex-wrap gap-y-4 md:gap-y-10">
            {leaderboards
              .filter((leaderboard) => {
                let reject = false;
                Object.keys(filters).forEach((category) => {
                  // if for a certain category, atleast 1 filter has been applied, none of which include the leaderboards filter for that category, return false
                  if (
                    (category === "mode" || category === "difficulty") &&
                    filters[category].length !== 0 &&
                    filters[category].indexOf(leaderboard[category]) === -1
                  ) {
                    reject = true;
                  }
                });
                return !reject;
              })
              .map((leaderboard) => (
                <Leaderboard
                  mode={leaderboard.mode}
                  metric={"Score"}
                  difficulty={leaderboard.difficulty}
                  key={leaderboard.mode + leaderboard.difficulty}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboards;
